import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Blog from './pages/Blog/Blog';
import BlogPost from './pages/Blog/BlogPost';
import About from './pages/About';
import Contact from './pages/Contact';
import Features from './pages/Features';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import EagleViewAlternative from './pages/EagleViewAlternative';
import CompetitorAlternative from './pages/CompetitorAlternative';
import DroneVsSatellite from './pages/DroneVsSatellite';
import GutterMeasurement from './pages/GutterMeasurement';
import RoofMeasurementSoftware from './pages/RoofMeasurementSoftware';
import LocationPage from './pages/locations/LocationPage';
import FreeAlternative from './pages/FreeAlternative';
import SharedReport from './pages/SharedReport';
import ResetPassword from './pages/ResetPassword';
import ActivateTrial from './pages/ActivateTrial';
import PitchCalculator from './pages/tools/PitchCalculator';
import GutterCalculator from './pages/tools/GutterCalculator';
import RoofSquaresCalculator from './pages/tools/RoofSquaresCalculator';
import NotFound from './pages/NotFound';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Measure = lazy(() => import('./pages/Measure'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const Admin = lazy(() => import('./pages/Admin'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const EagleViewPricing = lazy(() => import('./pages/EagleViewPricing'));

import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>}>
        <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/eagleview-alternative" element={<EagleViewAlternative />} />
              <Route path="/roofr-alternative" element={<CompetitorAlternative slug="roofr" />} />
              <Route path="/hover-alternative" element={<CompetitorAlternative slug="hover" />} />
              <Route path="/google-earth-roof-measurement-alternative" element={<CompetitorAlternative slug="google-earth" />} />
              <Route path="/quickmeasure-alternative" element={<CompetitorAlternative slug="quickmeasure" />} />
              <Route path="/eagleview-pricing" element={<EagleViewPricing />} />
              <Route path="/gutter-measurement" element={<GutterMeasurement />} />
              <Route path="/drone-vs-satellite-roof-measurement" element={<DroneVsSatellite />} />
              <Route path="/roof-measurement-software" element={<RoofMeasurementSoftware />} />
              <Route path="/locations/:citySlug" element={<LocationPage />} />
              <Route path="/free-alternative" element={<FreeAlternative />} />
              <Route path="/free" element={<FreeAlternative />} />
              <Route path="/shared/:shareToken" element={<SharedReport />} />
              <Route path="/tools/pitch-calculator" element={<PitchCalculator />} />
              <Route path="/tools/gutter-calculator" element={<GutterCalculator />} />
              <Route path="/tools/roof-squares-calculator" element={<RoofSquaresCalculator />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/activate" element={<ActivateTrial />} />
              <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
              <Route path="/accept-invite" element={<ProtectedRoute><AcceptInvite /></ProtectedRoute>} />
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/measure" element={<ProtectedRoute><Measure /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
        <Toaster richColors position="top-right" />
      </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
